import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import '../styles/PhotoModal.css'; // Import the new CSS file

const PhotoModal = ({ listing, onClose, isMobile }) => {
  // Renamed photoIndex to currentIndex, which refers to displayPhotos array
  const [currentIndex, setCurrentIndex] = useState(0);
  const [photos, setPhotos] = useState([]); // Actual filtered photos
  const [displayPhotos, setDisplayPhotos] = useState([]); // Photos with cloned ends for infinite loop
  const [isTransitionEnabled, setIsTransitionEnabled] = useState(true);

  const [slideWidth, setSlideWidth] = useState(0);
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isPerformingSeamJump, setIsPerformingSeamJump] = useState(false);
  const [isLandscape, setIsLandscape] = useState(false);

  const photoStripContainerRef = useRef(null);
  const startXRef = useRef(0);
  const startTimeRef = useRef(0);

  // Create a key based on media URLs for more robust change detection
  const mediaKey = React.useMemo(() => 
    (listing?.raw?.Media && Array.isArray(listing.raw.Media)) 
      ? listing.raw.Media.map(m => m?.MediaURL || '').join(',') 
      : '', 
  [listing?.raw?.Media]);

  // Effect to update photos and photoIndex when listing changes
  useEffect(() => {
    if (listing?.raw?.Media && Array.isArray(listing.raw.Media)) {
      const actualPhotos = listing.raw.Media.filter(m => m && m.MediaURL && m.Permission && Array.isArray(m.Permission) && m.Permission.includes('Public') && m.MediaCategory === 'Photo');
      
      setIsTransitionEnabled(false); // Disable transitions before setting photos and index
      setPhotos(actualPhotos);

      if (actualPhotos.length > 1) {
        const firstPhotoClone = { ...actualPhotos[0], id: `${actualPhotos[0].MediaKey || 'photo-0'}-clone-end` };
        const lastPhotoClone = { ...actualPhotos[actualPhotos.length - 1], id: `${actualPhotos[actualPhotos.length - 1].MediaKey || `photo-${actualPhotos.length - 1}`}-clone-start` };
        setDisplayPhotos([lastPhotoClone, ...actualPhotos, firstPhotoClone]);
        setCurrentIndex(1); // Start at the first actual photo
      } else {
        setDisplayPhotos(actualPhotos);
        setCurrentIndex(0);
      }

    } else {
      setIsTransitionEnabled(false); // Also handle for empty case
      setPhotos([]);
      setDisplayPhotos([]);
      setCurrentIndex(0);
      // Transitions will be re-enabled by useLayoutEffect if displayPhotos.length changes
    }
  }, [listing, mediaKey]);

  // Effect to calculate slide width (width of the container) using ResizeObserver
  useLayoutEffect(() => {
    const container = photoStripContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        setSlideWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(container);
    const initialWidth = container.offsetWidth;
    setSlideWidth(initialWidth);

    // slideWidth is set. Transitions will be enabled by a separate useEffect
    // when all initial layout conditions are met.

    return () => {
      resizeObserver.unobserve(container);
      resizeObserver.disconnect();
    };
  }, [displayPhotos.length]); // Key dependency

  // Effect to enable transitions once the initial layout is stable
  useEffect(() => {
    const initialCurrentIndex = photos.length > 1 ? 1 : 0;
    if (
      slideWidth > 0 &&
      displayPhotos.length > 0 &&
      currentIndex === initialCurrentIndex &&
      !isTransitionEnabled // Only act if transitions are currently off
    ) {
      // All conditions for a stable initial layout are met
      requestAnimationFrame(() => {
        setIsTransitionEnabled(true);
      });
    }
  }, [slideWidth, displayPhotos.length, currentIndex, photos.length, isTransitionEnabled, setIsTransitionEnabled]);

  // Effect to detect orientation changes on mobile
  useEffect(() => {
    if (!isMobile) {
      setIsLandscape(false); // Ensure landscape is false if not mobile
      return;
    }

    const mediaQuery = window.matchMedia('(orientation: landscape)');
    const handleOrientationChange = () => setIsLandscape(mediaQuery.matches);

    // Initial check
    handleOrientationChange();

    mediaQuery.addEventListener('change', handleOrientationChange);

    return () => {
      mediaQuery.removeEventListener('change', handleOrientationChange);
    };
  }, [isMobile]);

  // Effect for handling infinite loop seam jump
  useEffect(() => {
    // Check if we are on a cloned slide and need to jump
    // photos.length is the count of actual photos
    if (photos.length <= 1) return; // No jump needed for 0 or 1 actual photo

    const jumpTimeoutDuration = 350; // Must match CSS transition duration

    if (currentIndex === 0) { // On the cloned last slide (at the beginning)
      const timer = setTimeout(() => {
        setIsPerformingSeamJump(true);
        setIsTransitionEnabled(false);
        setCurrentIndex(photos.length); // Jump to the actual last slide's position in displayPhotos
        // Re-enable transition after the jump has been rendered, yielding to event loop then syncing with paint
        setTimeout(() => {
          requestAnimationFrame(() => {
            setIsTransitionEnabled(true);
            setIsPerformingSeamJump(false);
          });
        }, 0);
      }, jumpTimeoutDuration);
      return () => clearTimeout(timer);
    } else if (currentIndex === displayPhotos.length - 1) { // On the cloned first slide (at the end)
      const timer = setTimeout(() => {
        setIsPerformingSeamJump(true);
        setIsTransitionEnabled(false);
        setCurrentIndex(1); // Jump to the actual first slide's position in displayPhotos
        // Re-enable transition after the jump has been rendered, yielding to event loop then syncing with paint
        setTimeout(() => {
          requestAnimationFrame(() => {
            setIsTransitionEnabled(true);
            setIsPerformingSeamJump(false);
          });
        }, 0);
      }, jumpTimeoutDuration);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, photos.length, displayPhotos.length, setCurrentIndex, setIsPerformingSeamJump]);

  // Effect for keyboard listeners and body overflow
  const handlePrevPhoto = useCallback(() => {
    if (isPerformingSeamJump || photos.length <= 1) return; // No action if jumping or not enough photos for loop

    setCurrentIndex(prevIndex => {
      const newIndex = prevIndex - 1;
      // If moving to the cloned slide at the beginning (index 0)
      if (newIndex === 0) {
        setIsPerformingSeamJump(true); // Lock immediately
      }
      return newIndex;
    });
  }, [isPerformingSeamJump, photos.length, setIsPerformingSeamJump]);

  const handleNextPhoto = useCallback(() => {
    if (isPerformingSeamJump || photos.length <= 1) return; // No action if jumping or not enough photos for loop

    setCurrentIndex(prevIndex => {
      const newIndex = prevIndex + 1;
      // If moving to the cloned slide at the end
      // displayPhotos.length includes 2 clones, so actual photos.length + 2
      // The last index is displayPhotos.length - 1
      if (newIndex === displayPhotos.length - 1) {
        setIsPerformingSeamJump(true); // Lock immediately
      }
      return newIndex;
    });
  }, [isPerformingSeamJump, photos.length, displayPhotos.length, setIsPerformingSeamJump]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
                handlePrevPhoto();
      } else if (e.key === 'ArrowRight') {
        handleNextPhoto();
      }
    };
    if (photos.length > 0) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';

      return () => {
        window.removeEventListener('keydown', handleKeyDown);
        document.body.style.overflow = 'auto';
      };
    } else {
      // Ensure body overflow is reset if modal closes before photos load (e.g. quick escape)
      document.body.style.overflow = 'auto';
    }
  }, [onClose, handlePrevPhoto, handleNextPhoto, displayPhotos.length, photos.length]); // Added photos.length for the if (photos.length > 0) check

  // Return null if no listing or no photos to display
  if (!listing || !displayPhotos || displayPhotos.length === 0) return null;

  const handleTouchStart = (e) => {
    if (displayPhotos.length <= 1) return; // Or actual photos.length <=1 if clones aren't added for single photo

    startXRef.current = e.touches[0].clientX;
    startTimeRef.current = Date.now();
    setIsDragging(true);
  };

  const handleTouchMove = (e) => {
    if (!isDragging || photos.length <= 1) return; // Check against actual photos count
    const currentX = e.touches[0].clientX;
    let diff = currentX - startXRef.current;

    // Apply rubber band resistance at boundaries
    // Boundary check needs to consider actual photo indices if displayPhotos includes clones
    // For now, this might behave a bit off at extreme ends with clones, will refine with seam jump logic
    if ((currentIndex === 0 && diff > 0) || (currentIndex === displayPhotos.length - 1 && diff < 0)) {
      diff *= 0.3; // Reduce movement by 70%
    }
    setDragOffset(diff);
  };

  const handleTouchEnd = () => {
    if (!isDragging || photos.length <= 1) return; // Check against actual photos count
    setIsDragging(false);

    const endTime = Date.now();
    const duration = endTime - startTimeRef.current;
    const velocity = duration > 0 ? Math.abs(dragOffset) / duration : 0;

    // Quick flick detection (Instagram-like)
    if (velocity > 0.4) { // Adjusted velocity threshold slightly
      if (dragOffset < 0) {
        handleNextPhoto();
      } else if (dragOffset > 0) {
        handlePrevPhoto();
      }
    } else {
      // Fall back to distance threshold for slow swipes
      const threshold = slideWidth / 4;
      if (dragOffset < -threshold) { // Next photo
        handleNextPhoto();
      } else if (dragOffset > threshold) { // Prev photo
        handlePrevPhoto();
      }
    }
    setDragOffset(0); // Reset drag offset for the snap animation
  };

  // Add this handler to your PhotoModal component
  const handleBackdropPointerUp = (e) => {
    // console.log('[PhotoModal Backdrop] Event target:', e.target, 'isMobile:', isMobile);
    if (!isMobile) return; // Only allow backdrop close on mobile

    // Determine if the click target is one of the allowed elements for closing
    const targetIsOverlay = e.target === e.currentTarget;
    const targetIsPhotoStripContainer = photoStripContainerRef.current && e.target === photoStripContainerRef.current;
    const targetIsPhotoSlide = e.target.classList && e.target.classList.contains('photo-slide');

    if (!(targetIsOverlay || targetIsPhotoStripContainer || targetIsPhotoSlide)) {
      return;
    }
    
    // For touch: ignore if user was swiping (dragOffset > 5px)
    // The problem description uses dragOffset, but the component has startXRef and current X from touch/mouse events.
    // Assuming dragOffset is correctly maintained and reflects the total drag distance for the current interaction.
    if (e.pointerType === 'touch' && Math.abs(dragOffset) > 5) {
      return;
    }
    // --- Logic to prevent click-through by capturing the next click event ---
    const captureNextClick = (clickEvent) => {
      clickEvent.stopPropagation();
      clickEvent.preventDefault();
      // console.log('[PhotoModal] Captured and stopped click-through event on target:', clickEvent.target);
      document.removeEventListener('click', captureNextClick, true);
    };
    document.addEventListener('click', captureNextClick, true);

    // Fallback to remove the listener after a short delay, in case the click doesn't happen
    // or the component unmounts before the click occurs or the listener is otherwise not removed.
    setTimeout(() => {
      document.removeEventListener('click', captureNextClick, true);
      // console.log('[PhotoModal] Timeout: Removed captureNextClick listener as a fallback.');
    }, 50); // A short timeout, e.g., 50ms should be sufficient
    // --- End click-through prevention logic ---

    onClose();
    e.preventDefault(); // For the current pointerup event, prevent default actions like text selection or further event synthesis
    e.stopPropagation(); // For the current pointerup event, stop it from bubbling further
  };

  // Format address with fallbacks
  const getAddressParts = () => {
    let streetAddress = '';
    let cityStateZip = '';

    if (listing.unparsedAddress) {
      // Attempt to split unparsedAddress if it contains a comma, typical for full addresses
      const parts = listing.unparsedAddress.split(',');
      streetAddress = parts[0]?.trim();
      if (parts.length > 1) {
        cityStateZip = parts.slice(1).join(',').trim();
      }
    } else {
      streetAddress = listing.raw?.StreetNumber && listing.raw?.StreetName
        ? `${listing.raw.StreetNumber} ${listing.raw?.StreetDirPrefix || ''} ${listing.raw.StreetName} ${listing.raw?.StreetSuffix || ''}`.replace(/\s+/g, ' ').trim()
        : '';

      const city = listing.city || listing.raw?.City || '';
      const state = listing.state || listing.raw?.StateOrProvince || '';
      const postalCode = listing.postalCode || listing.raw?.PostalCode || '';
      cityStateZip = [city, state, postalCode].filter(Boolean).join(' ').trim();
    }
    
    return {
      streetAddress: streetAddress || 'Street address unavailable',
      cityStateZip: cityStateZip || 'Location unavailable'
    };
  };

  const addressParts = getAddressParts();

  // Format price with fallbacks
  const price = listing.price || listing.raw?.ListPrice;
  const formattedPrice = price ? `$${price.toLocaleString()}` : '';



// Return the modal portal
return ReactDOM.createPortal(
  <div
    className={`photo-modal-overlay ${isMobile && isLandscape ? 'mobile-landscape' : ''}`} onPointerUp={handleBackdropPointerUp}
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      zIndex: 1000,
    }}
  >
    {/* Top Bar (Header) */}
    <div
      className="photo-modal-header" onPointerUp={e => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '60px',
        padding: '0 20px',
        backgroundColor: 'rgba(0,0,0,0.85)',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        zIndex: 1100,
        boxSizing: 'border-box',
      }}
      onClick={e => e.stopPropagation()} // Prevent click on header from closing modal
    >
      <div className="photo-modal-header-info" style={{ flexGrow: 1, display: 'flex', alignItems: 'center', minWidth: 0 }}>
        {/* Left side: Address */}
        <div className="photo-modal-address-details" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{
            fontSize: '16px',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {addressParts.streetAddress}
          </div>
          {addressParts.cityStateZip && (
            <div style={{
              fontSize: '13px',
              color: 'rgba(255,255,255,0.8)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginTop: '2px'
            }}>
              {addressParts.cityStateZip}
            </div>
          )}
        </div>
        {/* Photo count - now part of header-info, to the right of address */}
        <div className="photo-modal-count-display" style={{
          fontSize: '14px',
          color: 'rgba(255,255,255,0.8)',
          whiteSpace: 'nowrap',
          minWidth: '70px',
          textAlign: 'right',
          marginLeft: '20px', // Space between address and count
          flexShrink: 0
        }}>
          {(photos.length > 1 && currentIndex > 0 && currentIndex <= photos.length ? currentIndex -1 : 0) + 1} of {photos.length}
        </div>
      </div>
      {/* Close button - direct child of photo-modal-header */}
      <button
        className="photo-modal-close-button" 
        onClick={onClose}
        style={{
          background: 'rgba(255, 255, 255, 0.1)',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          width: '36px',
          height: '36px',
          fontSize: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          outline: 'none',
          padding: 0,
          margin: 0, // Reset margin
          marginLeft: '20px', // Space between info block and close button
          lineHeight: 1,
          flexShrink: 0
        }}
        onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; }}
        onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'; }}
        onFocus={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'; }}
        onBlur={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'; }}
        aria-label="Close photo viewer"
      >
        ×
      </button>
    </div>

      {/* Main Image Display Area - Film Strip */}
      <div
        ref={photoStripContainerRef} // Add ref here
        className="photo-strip-container" // Styles for this will be in PhotoModal.css
        onTouchStart={photos.length > 1 ? handleTouchStart : undefined}
        onTouchMove={photos.length > 1 ? handleTouchMove : undefined}
        onTouchEnd={photos.length > 1 ? handleTouchEnd : undefined}
        style={{ // Dynamic cursor based on dragging state and photo count
          cursor: photos.length > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
        }}
        onClick={e => e.stopPropagation()} // Prevent click on image area from closing modal
      >
        {/* Navigation Arrows - Desktop Only */}
        {photos.length > 1 && (
          <>
            <button
              className="photo-modal-arrow prev-arrow desktop-only-arrow"
              onClick={(e) => {
                e.stopPropagation(); // Prevent modal close or other unintended actions
                handlePrevPhoto();
              }}
              aria-label="Previous photo"
            >
              {'<'}
            </button>
            <button
              className="photo-modal-arrow next-arrow desktop-only-arrow"
              onClick={(e) => {
                e.stopPropagation(); // Prevent modal close or other unintended actions
                handleNextPhoto();
              }}
              aria-label="Next photo"
            >
              {'>'}
            </button>
          </>
        )}

        <div
          className={`photo-strip ${isDragging ? 'dragging' : ''} ${!isTransitionEnabled ? 'no-transition' : ''}`}
          style={{
            transform: `translateX(${-currentIndex * slideWidth + dragOffset}px)`,
            width: slideWidth > 0 && displayPhotos.length > 0 ? `${slideWidth * displayPhotos.length}px` : 'auto',
            transition: isTransitionEnabled ? undefined : 'none', // Dynamically control transition
          }}
        >
          {displayPhotos.map((photo, index) => {
            const shouldLoad = Math.abs(index - currentIndex) <= 1;
            const photoURL = photo?.MediaURL; 
            // Use the unique ID generated for clones, or MediaKey for actual photos
            const photoKey = photo?.id || photo?.MediaKey || `slide-${index}-${photoURL || ''}`;

            return (
              <div className="photo-slide" key={photoKey} style={{ width: slideWidth > 0 ? `${slideWidth}px` : '100%' }}>
                {shouldLoad && photoURL ? (
                  <img
                    src={photoURL}
                    alt={`Photo ${index + 1}`}
                    className="photo-modal-image"
                  />
                ) : (
                  <div className="photo-placeholder" style={{ width: '100%', height: '100%' }} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Bottom Bar (Footer) */}
      <div
        className="photo-modal-footer"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '60px',
          padding: '0 20px',
          backgroundColor: 'rgba(0,0,0,0.85)',
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 1100,
          boxSizing: 'border-box',
        }}
        onPointerUp={e => e.stopPropagation()} // Prevent click on footer from closing modal
      >
        {/* Left side: Price, Bed/Bath/Sqft */}
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          {formattedPrice && (
            <div style={{
              fontSize: '16px',
              fontWeight: 'bold',
            }}>
              {formattedPrice}
            </div>
          )}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginTop: formattedPrice ? '4px' : '0', // Add margin only if price is present
            fontSize: '13px',
            color: 'rgba(255,255,255,0.9)',
            whiteSpace: 'nowrap',
          }}>
            {(listing.beds || listing.BedroomsTotal) && <span>{listing.beds || listing.BedroomsTotal} {isMobile ? 'bd' : 'bed'}</span>}
            {(listing.beds || listing.BedroomsTotal) && (listing.baths || listing.BathroomsFull) ? <span style={{color: 'rgba(255,255,255,0.7)', margin: '0 0'}}>·</span> : ''}
            {(listing.baths || listing.BathroomsFull) && <span>{listing.baths || listing.BathroomsFull} {isMobile ? 'ba' : 'bath'}</span>}
            {(listing.baths || listing.BathroomsFull) && (listing.sqft || listing.LivingArea) ? <span style={{color: 'rgba(255,255,255,0.7)', margin: '0 0'}}>·</span> : ''}
            {(listing.sqft || listing.LivingArea) && <span>{(listing.sqft || listing.LivingArea).toLocaleString()} sqft</span>}
          </div>
        </div>
        {/* Right side: MLS Number */}
        <div style={{
          fontSize: '14px',
          color: 'rgba(255,255,255,0.8)',
          whiteSpace: 'nowrap',
          minWidth: '70px',
          textAlign: 'right',
          flexShrink: 0
        }}>
          {listing?.mlsIdentifier ? `MLS # ${listing.mlsIdentifier}` : ''}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PhotoModal;
